import { useEffect, useRef, useState } from 'react'
import { useDataProvider } from 'react-admin'

// Datev's OAuth flow is uniquely two-step: round 1 mints a short-lived token
// we use to fetch the user's accessible Datev clients, so we can present them
// as a dropdown before kicking off round 2. This hook drives that fetch.
const DATEV_CONNECTOR_NAMES = ['datev_oauth', 'datev_legacy']
export const isDatevConnector = (name) => DATEV_CONNECTOR_NAMES.includes(name)
export const PENDING_SECOND_OAUTH_STATUS = 'Pending second OAuth'

const NO_CLIENTS_MESSAGE =
    'Datev returned no clients for this account, so there is nothing to ' +
    'select. Grant the Datev user access to at least one client, then click ' +
    'Start over.'

// 401/403 are the only statuses that mean "you are not allowed to ask this
// product" -- everything else (timeout, DNS, 5xx, 429, no status at all) is a
// transient failure and must not send admins hunting for a config problem.
const REJECTION_STATUSES = [401, 403]

const describe = (errors) =>
    errors
        .map((e) => `${e.product}${e.status ? ` (${e.status})` : ''}`)
        .join(', ')

/**
 * Copy for an empty Datev client picker. Datev serves the client list per API
 * product, so an empty picker has several very different causes: the account
 * really has no clients, the products we asked rejected us (401/403 = the Datev
 * app or user isn't entitled to that product, which is what a sandbox-registered
 * app hitting the production API looks like), or a product simply couldn't be
 * reached. Name the products and say which kind of failure it was, so the
 * message points at the fixable thing -- and doesn't invent one when it can't.
 */
export const datevNoClientsMessage = (clientErrors) => {
    const errors = (clientErrors || []).filter((e) => e?.product)
    if (!errors.length) {
        return NO_CLIENTS_MESSAGE
    }
    const rejected = errors.filter((e) => REJECTION_STATUSES.includes(e.status))
    const unreachable = errors.filter(
        (e) => !REJECTION_STATUSES.includes(e.status)
    )

    const parts = ['Datev returned no clients for this account.']
    if (rejected.length) {
        parts.push(
            'These Datev product APIs rejected the request: ' +
                describe(rejected) +
                '. That usually means the Datev app or user is not entitled ' +
                'to those products, or the connector is pointed at the wrong ' +
                'Datev environment.'
        )
    }
    if (unreachable.length) {
        parts.push(
            'These Datev product APIs could not be reached: ' +
                describe(unreachable) +
                '. That is usually temporary, so the client list may just be ' +
                'incomplete.'
        )
    }
    parts.push(
        rejected.length
            ? "Check the connector setup and the Datev user's client access, " +
                  'then click Start over.'
            : 'Click Start over to try again.'
    )
    return parts.join(' ')
}

/**
 * Datev-only: once the tenant lands in "Pending second OAuth", fetch the
 * non-sensitive secrets (which contain the Datev clients list) so the
 * post-OAuth selection UI can be populated.
 *
 * Returns:
 *   - authStatus: the current `<CONNECTOR>_AUTH_STATUS` for the tenant
 *   - datevClients: array of Datev clients available to the user, or null
 *   - datevClientErrors: array of `{product, status}` for the Datev product APIs
 *     that failed the client lookup, or null. `status` is 401/403 for a refused
 *     product and null (or a 5xx) for one we couldn't reach. Only ever populated
 *     when the client list came back empty, so an empty picker can name its real
 *     cause.
 *
 * Optional callbacks (latest-value via ref, so they don't need to be memoized):
 *   - onLoaded(clients): fired once per successful fetch
 *   - onError(error):    fired once per failed fetch
 */
export const useDatevClients = ({
    connector,
    tenant,
    enabled = true,
    onLoaded,
    onError,
}) => {
    const dataProvider = useDataProvider()
    const [datevClients, setDatevClients] = useState(null)
    const [datevClientErrors, setDatevClientErrors] = useState(null)

    const onLoadedRef = useRef(onLoaded)
    const onErrorRef = useRef(onError)
    useEffect(() => {
        onLoadedRef.current = onLoaded
        onErrorRef.current = onError
    })

    const isDatev = isDatevConnector(connector?.name)
    const tenantId = tenant?.id
    const authStatus =
        isDatev && tenant
            ? tenant?.status?.auth?.[
                  `${connector.name.toUpperCase()}_AUTH_STATUS`
              ]
            : null

    useEffect(() => {
        if (!enabled || !isDatev || !tenantId) return

        if (authStatus !== PENDING_SECOND_OAUTH_STATUS) {
            setDatevClients((prev) => (prev ? null : prev))
            setDatevClientErrors((prev) => (prev ? null : prev))
            return
        }

        let cancelled = false
        dataProvider
            .V1_ACTION('tenants', {
                id: tenantId,
                action: `connectors/${connector.name}/sanitized_secrets`,
                method: 'GET',
            })
            .then((res) => {
                if (cancelled) return
                const clients = res.data?.clients || []
                setDatevClients(clients)
                setDatevClientErrors(res.data?.clients_fetch_errors || null)
                onLoadedRef.current?.(clients)
            })
            .catch((err) => {
                if (cancelled) return
                console.debug(err)
                onErrorRef.current?.(err)
            })

        return () => {
            cancelled = true
        }
    }, [enabled, isDatev, authStatus, tenantId, dataProvider])

    return { authStatus, datevClients, datevClientErrors }
}
